// Captura de firma de un TESTIGO — Bloque D del formulario oficial 024
// (Consentimiento Informado, Fase 3): si el paciente rechaza el
// procedimiento propuesto Y se niega a firmar el propio documento de
// negativa, un testigo firma en su lugar (nombre completo + cédula, sin
// FHIR `Patient` propio — a diferencia del paciente, estos datos no vienen
// de ningún recurso existente, hay que capturarlos acá). Generalizado a los
// 7 códigos de consentimiento (ver lib/consentimientos.ts), no solo 024 —
// mismo criterio que el resto de MotorFormularioConsentimiento.tsx.
import type { JSX } from 'react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SignaturePad, type SignaturePadHandle } from './SignaturePad';

export interface FirmaTestigoCapturada {
  nombre: string;
  cedula: string;
  dataUrl: string;
  contentType: string;
}

export interface CapturaFirmaTestigoProps {
  onCapturada: (firma: FirmaTestigoCapturada) => void;
}

export function CapturaFirmaTestigo({ onCapturada }: CapturaFirmaTestigoProps): JSX.Element {
  const { t } = useTranslation();
  const sigRef = useRef<SignaturePadHandle>(null);
  const [nombre, setNombre] = useState('');
  const [cedula, setCedula] = useState('');
  const [sigVacia, setSigVacia] = useState(true);
  const [error, setError] = useState<string | null>(null);

  function confirmar(): void {
    if (!nombre.trim() || !cedula.trim()) {
      setError(t('consentimiento.faltanDatosTestigo'));
      return;
    }
    if (!sigRef.current || sigRef.current.isEmpty()) {
      setError(t('consentimiento.firmaVacia'));
      return;
    }
    setError(null);
    onCapturada({ nombre: nombre.trim(), cedula: cedula.trim(), dataUrl: sigRef.current.toDataUrl(), contentType: 'image/png' });
  }

  return (
    <div className="flex flex-col gap-3">
      {error && (
        <Alert variant="red">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1">
          <Label htmlFor="testigo-nombre">{t('consentimiento.testigoNombre')}</Label>
          <Input id="testigo-nombre" value={nombre} onChange={(e) => setNombre(e.target.value)} />
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor="testigo-cedula">{t('consentimiento.testigoCedula')}</Label>
          <Input id="testigo-cedula" value={cedula} onChange={(e) => setCedula(e.target.value)} />
        </div>
      </div>
      <SignaturePad ref={sigRef} onChange={setSigVacia} />
      <div className="flex justify-end">
        <Button type="button" onClick={confirmar} disabled={sigVacia}>
          {t('consentimiento.confirmarFirma')}
        </Button>
      </div>
    </div>
  );
}
